import {
  Box,
  Chip,
  ListItemButton,
  type ListItemProps,
  ListItemText,
  useTheme,
} from '@mui/material';
import { type ReactNode, useState } from 'react';

import type { PortalTheme } from '../theme';

export interface ListItemCardProps {
  primaryText: ReactNode;
  secondaryText?: ReactNode;
  tertiaryText?: ReactNode;
  listItemProps?: ListItemProps;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  href?: string;
  onClick?: () => void;
  cancelHoverOnEndAdornment?: boolean;
  ariaLabel?: string;
  tags?: string[];
}

export const ListItemCard = (props: ListItemCardProps) => {
  const theme = useTheme<PortalTheme>();
  const [isCancelHover, setIsCancelHover] = useState(false);

  const isClickable = props.href || props.onClick;
  return (
    <ListItemButton
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.divider}`,
        p: 1.5,
        boxShadow: theme.boxShadow,
        backgroundColor: theme.palette.background.paper,

        '&:hover': !isClickable
          ? { backgroundColor: theme.palette.background.paper }
          : isCancelHover
            ? { backgroundColor: 'transparent' }
            : undefined,
        '&:active': !isClickable
          ? { backgroundColor: theme.palette.background.paper }
          : undefined,
        cursor: !props.onClick && !props.href ? 'default' : undefined,
      }}
      role={isClickable ? 'button' : 'presentation'}
      disableRipple={!isClickable}
      component={props.href ? 'a' : props.onClick ? 'button' : 'div'}
      {...(props.href && {
        target: '_blank',
        rel: 'noopener noreferrer',
        href: props.href,
      })}
      onClick={(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (props.href && props.onClick) {
          e.preventDefault();
        }

        props.onClick?.();
      }}
      {...props.listItemProps}
      aria-label={props.ariaLabel}
    >
      {props.startAdornment}
      <Box flexDirection="column" gap={1} sx={{ display: 'flex', flexGrow: 1 }}>
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          <Box flexDirection="column" sx={{ display: 'flex', flexGrow: 1 }}>
            <ListItemText
              primary={props.primaryText}
              slotProps={{
                primary: {
                  color:
                    props.listItemProps?.color || theme.palette.text.primary,
                },
              }}
              secondary={props.secondaryText}
              sx={{
                ml: props.startAdornment ? 1 : 0,
                my: 0,
                pr: props.endAdornment ? 2 : 0,
                width: props.endAdornment ? 'fit-content' : undefined,
                textAlign: 'start',
              }}
            />
            {props.tertiaryText && (
              <ListItemText
                secondary={props.tertiaryText}
                sx={{
                  ml: props.startAdornment ? 1 : 0,
                  my: 0,
                  pr: props.endAdornment ? 2 : 0,
                  width: props.endAdornment ? 'fit-content' : undefined,
                  textAlign: 'start',
                }}
              />
            )}
          </Box>
          <Box
            sx={{ marginBottom: 'auto' }}
            onMouseEnter={() => {
              if (props.cancelHoverOnEndAdornment) {
                setIsCancelHover(true);
              }
            }}
            onMouseLeave={() => {
              if (props.cancelHoverOnEndAdornment) {
                setIsCancelHover(false);
              }
            }}
          >
            {props.endAdornment}
          </Box>
        </Box>
        {props.tags && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {props.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Box>
        )}
      </Box>
    </ListItemButton>
  );
};
